import styled, { css } from "styled-components";
import { Typography as BaseTypography, Checkbox as BaseCheckbox, BlueBirdTheme } from "@kitbird/core";

interface StyledCardProps {
  variant?: "primary" | "nested";
  isScrollable?: boolean;
}

interface LayoutContainerProps {
  layout?: "grid" | "flex";
  columns?: string;
  direction?: "row" | "column";
  align?: string;
  gap?: string;
  marginTop?: string;
}

interface SelectionGroupBodyProps {
  gridTemplateColumns?: string;
  gridTemplateRows?: string;
  gap?: string;
}

export const ActionSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
`;

export const StyledFormSectionCard = styled.div<StyledCardProps>`
  border: 1px solid ${() => BlueBirdTheme.colors.secondary[200]};
  border-radius: 8px;
  padding: 24px;
  background-color: ${({ variant }) => (variant === "nested" ? BlueBirdTheme.colors.gray.background : "#FFF")};

  ${({ isScrollable, variant }) => isScrollable && css`
    overflow-y: auto;
    max-height: ${variant === "primary" ? "60vh" : "30vh"};
  `}
`;

export const CardHeader = styled.div<{ alignment: "start" | "between" }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  justify-content: ${({ alignment }) => (alignment === "start" ? "flex-start" : "space-between")};
  margin-bottom: 20px;
`;

export const CardContentGrid = styled.div<{ layout?: { type: "grid"; columns: string; gap?: string; } }>`
  ${({ layout }) => layout && css`
    display: grid;
    grid-template-columns: ${layout.columns};
    gap: ${layout.gap || (layout.type === "grid" ? "24px" : "4px")};
  `}
`;

export const Typography = styled(BaseTypography)`
    color: ${({ color }) => color || "inherit"};
    font-weight: ${({ fontWeight }) => fontWeight || "normal"};
`;

// Styled components for hierarchical grouping in steps 3, 4, 5
export const GroupWrapper = styled.div`
  margin-bottom: 24px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const GroupTitle = styled(Typography)`
  margin-bottom: 15px;
`;

export const NestedCardGrid = styled.div<{ columns?: string }>`
  display: grid;
  gap: 24px;
  grid-template-columns: ${({ columns }) => columns || "repeat(auto-fit, minmax(284px, 1fr))"};
`;

export const LayoutContainer = styled.div<LayoutContainerProps>`
  display: ${({ layout = "flex" }) => layout};
  gap: ${({ gap = "24px" }) => gap};
  ${({ marginTop }) => marginTop && `margin-top: ${marginTop};`}

  ${({ layout, columns }) => layout === "grid" && `
    grid-template-columns: ${columns || "1fr"};
  `}

  ${({ layout, direction, align }) => layout === "flex" && `
    flex-direction: ${direction || "column"};
    ${align ? `align-items: ${align};` : ""}
  `}
`;

export const InlineFlexWrapper = styled.div`
  display: inline-flex;
  gap: 5px;
  align-items: center;
`;

export const SelectionGroupContainer = styled.div`
  border: 1px solid ${BlueBirdTheme.colors.secondary[200]};
  border-radius: 8px;
  padding: 24px;
  background-color: ${BlueBirdTheme.colors.gray.background};
  overflow-y: auto;
  max-height: 30vh;
`;

export const SelectionGroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-bottom: 20px;
`;

export const SelectionGroupBody = styled.div<SelectionGroupBodyProps>`
  display: grid;
  gap: ${(props) => props.gap || "4px"};
  grid-template-columns: ${(props) => props.gridTemplateColumns
    || "repeat(auto-fit, minmax(284px, 1fr))"};
  grid-template-rows: ${(props) => props.gridTemplateRows
    || "auto 1fr"};
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 40vh;
  width: 100%;
`;

export const CustomCheckbox = styled(BaseCheckbox)`
  span {
    white-space: normal !important;
  }
`;
